import type { z } from 'zod'
import type { dataSourceSchema, documentSetupSchema, websiteScraperSchema } from './config'

export type DataSourceFormValues = z.infer<typeof dataSourceSchema>
export type DocumentSetupFormValues = z.infer<typeof documentSetupSchema>
export type WebsiteScraperPayload = z.infer<typeof websiteScraperSchema>

export type SyncFrequencyPeriod = 'WEEK' | 'MONTH'

// Retrieval profile a KB is created with — mirrors rag's RetrievalProfile ('default' | 'docs').
// 'default' = top-5, no long-query rewrite (voice-friendly). 'docs' = top-20 + long-query rewrite,
// tuned for large documentation corpora answered on the chat channel (higher retrieval latency).
export type KBRetrievalProfile = 'default' | 'docs'

export type WebsiteScraperFormValues = WebsiteScraperPayload & {
  links: { title: string; url: string; selected: boolean; markdown: string; id: string }[]
  isLoading: boolean
  jobId?: string
}

export enum KBDocumentSource {
  FILE = 'file',
  URL = 'url',
  APP = 'app',
}

export interface KBDocument {
  display_name: string | null
  type: KBDocumentSource.URL | KBDocumentSource.FILE
  document_id: string
}

export interface KBAppDocument {
  type: 'collection' | 'article'
  id: string
}

export interface CreateKBSource {
  source_type: KBDocumentSource
  config: {
    source_type: KBDocumentSource
    url: string
    max_depth: number
    max_pages: number
    crawl_subpages: boolean
    exclude_paths: string[]
    include_paths: string[]
    app_source: string | null
    app_source_id?: string | null
    app_data: KBDocument[] | KBAppDocument[] | null
  }
  documents?: KBDocument[] | KBAppDocument[]
}

export interface CreateKBPayload {
  name: string
  profile: KBRetrievalProfile
  phlo_uuids: string[]
  crawl_job_id?: string
  include_image_content: boolean
  include_youtube_content: boolean
  sync_period: SyncFrequencyPeriod
  sync_frequency: number
  source: CreateKBSource
  url_selection_type: 'all' | 'selected'
  sync_enabled: boolean
  type: KBDocumentSource
}

export type DocumentStatus = 'queued' | 'syncing' | 'completed' | 'failed' | 'completed_with_errors'
// A KB/source rollup CAN be 'completed_with_errors' (partial success) — the value is produced at
// runtime by the rag adapter. Keep KBStatus == DocumentStatus so the field type matches reality
// (the old Exclude<> was a type lie that `as KBStatus` casts had to paper over).
export type KBStatus = DocumentStatus

export interface RKnowledgeBaseDocument {
  id: string
  document_type: KBDocumentSource.URL
  name: string
  source: null
  remote_id: null
  embedding_status: DocumentStatus
  display_name: string
  enabled: boolean
  updated_at: string
  updated_by: {
    uuid: string
    name: string
  }
  last_sync_attempt_at: string
  last_synced_successfully_at: string
  embedding_error: string | null
  content_hash: string
  chunk_count?: number // chunks embedded for this doc; 0 on a "completed" doc means it produced nothing
  // The page genuinely had no content (rag status "empty") — BENIGN, not a failure. Distinguishes an
  // empty page from a real 0-chunk embed FAILURE so the row shows a muted "Empty", not a red "No content".
  is_empty?: boolean
  download_url?: string | null // the doc's fetchable file URL (file sources) — used by the Download action
}

export interface CreateKBResponse {
  knowledge_base_id: string
}

export interface RKnowledgeBase {
  id: string
  name: string
  phlo_uuids: string[]
  // True when the agent-link reverse-read (PHLO-core) FAILED for this list, so `phlo_uuids` was
  // coalesced to [] and is NOT authoritative. Link editors must refuse a full-replace when set —
  // otherwise a transient read failure silently drops the agent's existing KB associations.
  linksUnavailable?: boolean
  enabled: boolean
  updated_at: string
  updated_by: {
    uuid: string
    name: string
  }
  include_youtube_content: boolean
  include_image_content: boolean
  url_selection_type: 'all' | 'selected'
  sync_period: SyncFrequencyPeriod
  sync_frequency: number
  sync_enabled: boolean
  embedding_status: KBStatus
  // KB-wide total embedded chunks (summed across sources). Used as the UNCAPPED, monotonically-climbing
  // progress signal for the detail-page sync poller (the per-source documents[] is capped, so it can't
  // be trusted to keep changing through a long sync).
  chunk_count?: number
  // First failure cause (when embedding_status is failed/partial) — shown on hover of the list badge.
  error_detail?: string | null
  sources: Array<{
    id: string
    source_type: KBDocumentSource
    documents: Array<RKnowledgeBaseDocument>
    // TRUE totals — `documents` is CAPPED (top-N, errored-first) for large sources, so the badge / the
    // "Showing X of N" footer must use these, not documents.length.
    document_count?: number
    error_count?: number
    empty_count?: number // benign no-content pages (NOT failures) — drives a muted "N empty"
    enabled: boolean
    embedding_status: KBStatus
    fetching: boolean
    error_detail?: string | null
    config: {
      source_type: KBDocumentSource
      url: string
      original_filename?: string | null
      max_depth: number
      max_pages: number
      crawl_subpages: boolean
      exclude_paths: string[]
      include_paths: string[]
      app_source: string | null
      app_source_id?: string | null
      app_data?: KBDocument[] | KBAppDocument[] | null
    }
  }>
  documents: Array<RKnowledgeBaseDocument>
}

export interface NewKBFormValues
  extends DocumentSetupFormValues,
    DataSourceFormValues,
    WebsiteScraperFormValues {
  step: number
  optionsAccordionOpen: string[]
}

export interface AddDocumentPayload {
  documents?: KBDocument[]
  crawl_job_id?: string | null
  app_source: string | null
  app_source_id?: string | null
  source?: CreateKBSource
}

export interface SyncDocumentPayload {
  documentId: string
  kbId: string
}

export interface SyncSourcePayload {
  sourceId: string
  kbId: string
}
export interface RPage {
  id: string
  url: string
  title: string
  markdown: string
}
export interface RCrawlSiteStatus {
  status: 'completed' | 'in_progress'
  pages: RPage[]
}

export interface McpArticle {
  type: 'article'
  id: string
  title: string
  url: string | null
  content: string
  content_type: 'text' | 'markdown' | 'html'
}

export interface McpArticleCollection {
  type: 'collection'
  id: string
  title?: string
  description?: string
  url?: string
  items: Array<McpArticle | McpArticleCollection>
}
